import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { setClientOutTime } from '../../../services/private/site-visit.service';

const SetOutTimeModal = ({ show, closeModal, visit, onSuccess }) => {
    const [selectedDateTime, setSelectedDateTime] = useState(new Date());
    const [loading, setLoading] = useState(false);

    // Calculate minimum allowed date/time based on check-in time
    const getMinDate = () => {
        if (!visit?.client_in_time) return new Date();
        const inDate = new Date(visit.client_in_time);
        return isNaN(inDate.getTime()) ? new Date() : inDate;
    };

    useEffect(() => {
        if (show) {
            const minAllowed = getMinDate();
            const now = new Date();
            // Default to now, or minAllowed if check-in was in future
            setSelectedDateTime(now < minAllowed ? minAllowed : now);
        }
    }, [show, visit]);

    const getInitials = (name) => {
        if (!name) return 'CU';
        const parts = name.trim().split(' ').filter(Boolean);
        if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
        return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    };

    const formatInTime = (isoString) => {
        if (!isoString) return 'N/A';
        try {
            const date = new Date(isoString);
            const timeStr = date.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true, timeZone: 'Asia/Kolkata' });
            const dateStr = date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', timeZone: 'Asia/Kolkata' });
            return `${dateStr} • ${timeStr}`;
        } catch (e) {
            return isoString;
        }
    };

    const getMinTime = () => {
        const minDate = getMinDate();
        if (!selectedDateTime) return minDate;
        
        // If selected date is the same day as check-in, set minTime to exact check-in time
        if (selectedDateTime.toDateString() === minDate.toDateString()) {
            return minDate;
        }
        
        // Otherwise, allow from 12:00 AM (00:00:00)
        const d = new Date(selectedDateTime);
        d.setHours(0, 0, 0, 0);
        return d;
    };

    const getMaxTime = () => {
        const now = new Date();
        if (!selectedDateTime) return now;

        // If selected date is today, restrict maxTime to current time
        if (selectedDateTime.toDateString() === now.toDateString()) {
            return now;
        }

        // Otherwise, allow up to 11:59 PM (23:59:59)
        const d = new Date(selectedDateTime);
        d.setHours(23, 59, 59, 999);
        return d;
    };

    if (!show || !visit) return null;

    const handleSave = async () => {
        if (!selectedDateTime || isNaN(selectedDateTime.getTime())) {
            toast.error("Please select a valid Out Date & Time");
            return;
        }

        const minAllowed = getMinDate();
        if (selectedDateTime < minAllowed) {
            toast.error(`Out time cannot be earlier than check-in time (${formatInTime(visit.client_in_time)})`);
            return;
        }

        const isoTimestamp = selectedDateTime.toISOString();
        setLoading(true);

        try {
            const res = await setClientOutTime(visit.uuid, { client_out_time: isoTimestamp });
            if (res?.data?.status === 200 || res?.status === 200) {
                toast.success(res?.data?.message || "Client out time saved successfully");
                if (onSuccess) onSuccess(visit.uuid);
                closeModal();
            } else {
                toast.error(res?.data?.message || "Failed to save out time");
            }
        } catch (err) {
            const errMsg = err?.response?.data?.message || "Something went wrong";
            toast.error(errMsg);
            if (errMsg.toLowerCase().includes("already") || err?.response?.status === 404 || err?.response?.status === 400) {
                if (onSuccess) onSuccess(visit.uuid);
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-black bg-opacity-50 p-4">
            <div className="bg-white rounded-3xl shadow-2xl w-full max-w-[480px] p-4 sm:p-5 animate-in fade-in duration-150">
                
                {/* Header */}
                <div className="flex items-center justify-between border-b border-gray-100 pb-3 mb-3.5">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-2xl bg-[#F0EEFF] text-[#635BFF] flex items-center justify-center flex-shrink-0">
                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                <circle cx="12" cy="12" r="10" />
                                <polyline points="12 6 12 12 16 14" />
                            </svg>
                        </div>
                        <div>
                            <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-0">Set Out Time</h3>
                            <p className="text-xs text-gray-400 mb-0 mt-0.5">Update the out time for your current site visit.</p>
                        </div>
                    </div>
                    <button
                        onClick={closeModal}
                        disabled={loading}
                        className="w-8 h-8 sm:w-9 sm:h-9 border border-gray-200 rounded-xl bg-white hover:bg-gray-50 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="18" y1="6" x2="6" y2="18"></line>
                            <line x1="6" y1="6" x2="18" y2="18"></line>
                        </svg>
                    </button>
                </div>

                {/* Customer & Visit Info Banner Card */}
                <div className="bg-[#F7F6FF] rounded-2xl p-3 border border-[#EBE8FF] flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
                    {/* Top/Left: Initials Avatar + Name + Mobile */}
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                        <div className="w-10 h-10 rounded-full bg-[#EBF3FF] text-[#3B82F6] font-bold text-xs flex items-center justify-center flex-shrink-0 shadow-xs">
                            {getInitials(visit.customer_name)}
                        </div>
                        <div className="flex flex-col justify-center min-w-0 flex-1">
                            <p className="font-bold text-gray-900 text-sm mb-0 leading-tight truncate w-full" title={visit.customer_name || 'Customer'}>
                                {visit.customer_name || 'Customer'}
                            </p>
                            {visit.customer_mobile && (
                                <p className="text-xs font-semibold text-[#3B82F6] leading-tight mb-0 mt-0.5">
                                    {visit.customer_mobile}
                                </p>
                            )}

                            {/* Mobile Only: Check-in Time under mobile number */}
                            <div className="sm:hidden pt-2 mt-1.5 border-t border-[#E0D9FF] flex flex-col">
                                <span className="text-[9px] font-bold text-gray-400 uppercase tracking-wider block mb-0.5">
                                    CHECK-IN TIME
                                </span>
                                <span className="text-xs font-semibold text-gray-800 leading-tight">
                                    {formatInTime(visit.client_in_time)}
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Vertical Separator (Desktop/Tablet only) */}
                    <div className="hidden sm:block w-[1px] h-8 bg-[#D3CCFF] self-center flex-shrink-0" />

                    {/* Right: Check-in Time (Desktop/Tablet only) */}
                    <div className="hidden sm:flex flex-col justify-center flex-shrink-0">
                        <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-0.5">
                            CHECK-IN TIME
                        </span>
                        <span className="text-xs font-semibold text-gray-800 leading-tight">
                            {formatInTime(visit.client_in_time)}
                        </span>
                    </div>
                </div>

                {/* Custom Date-Time Picker Input */}
                <div className="space-y-1.5 mb-4">
                    <label className="text-xs sm:text-sm font-semibold text-gray-800">Select Out Date & Time</label>
                    <div className="border border-gray-200 rounded-xl sm:rounded-2xl p-2 sm:p-2.5 bg-white flex items-center gap-2.5 shadow-xs hover:border-[#635BFF] transition-colors">
                        <div className="w-8 h-8 rounded-lg sm:rounded-xl bg-[#F0EEFF] text-[#635BFF] flex items-center justify-center flex-shrink-0">
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                                <line x1="16" y1="2" x2="16" y2="6" />
                                <line x1="8" y1="2" x2="8" y2="6" />
                                <line x1="3" y1="10" x2="21" y2="10" />
                            </svg>
                        </div>
                        <div className="w-full text-xs sm:text-sm font-semibold text-gray-800">
                            <DatePicker
                                selected={selectedDateTime}
                                onChange={(date) => {
                                    if (!date) return;
                                    const minAllowed = getMinDate();
                                    const now = new Date();

                                    let adjustedDate = new Date(date);

                                    // If user selects the check-in date, jump directly to check-in time (e.g. 5:05 PM) if currently selected time is earlier
                                    if (adjustedDate.toDateString() === minAllowed.toDateString()) {
                                        if (adjustedDate < minAllowed) {
                                            adjustedDate = new Date(minAllowed);
                                        }
                                    }

                                    // If user selects today and time is in future, clamp to current time
                                    if (adjustedDate.toDateString() === now.toDateString()) {
                                        if (adjustedDate > now) {
                                            adjustedDate = new Date(now);
                                        }
                                    }

                                    setSelectedDateTime(adjustedDate);
                                }}
                                showTimeSelect
                                timeFormat="hh:mm aa"
                                timeIntervals={15}
                                timeCaption="Time"
                                dateFormat="dd MMM yyyy, hh:mm aa"
                                minDate={getMinDate()}
                                maxDate={new Date()}
                                minTime={getMinTime()}
                                maxTime={getMaxTime()}
                                filterTime={(time) => {
                                    const minAllowed = getMinDate();
                                    const now = new Date();
                                    // If same day as check-in, reject times before check-in time
                                    if (time.toDateString() === minAllowed.toDateString()) {
                                        return time.getTime() >= minAllowed.getTime();
                                    }
                                    // If same day as today, reject times in future
                                    if (time.toDateString() === now.toDateString()) {
                                        return time.getTime() <= now.getTime();
                                    }
                                    return true;
                                }}
                                popperPlacement="bottom"
                                popperModifiers={[
                                    {
                                        name: 'preventOverflow',
                                        options: {
                                            rootBoundary: 'viewport',
                                            tether: false,
                                            altAxis: true,
                                        },
                                    },
                                ]}
                                className="w-full text-xs sm:text-sm font-semibold text-gray-800 bg-transparent focus:outline-none cursor-pointer"
                                disabled={loading}
                            />
                        </div>
                    </div>
                    <span className="text-[11px] text-gray-400 block pt-0.5">
                        * Selection is allowed between check-in time and current time.
                    </span>
                </div>

                {/* Actions */}
                <div className="flex items-center justify-between gap-3 mt-4 pt-3.5 border-t border-gray-100">
                    <button
                        type="button"
                        onClick={closeModal}
                        disabled={loading}
                        className="w-1/2 py-2.5 border border-gray-200 hover:bg-gray-50 text-gray-700 rounded-2xl font-semibold text-xs sm:text-sm transition-colors text-center"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={handleSave}
                        disabled={loading}
                        className="w-1/2 py-2.5 bg-gradient-to-r from-[#635BFF] to-[#3B37F5] hover:opacity-95 text-white rounded-2xl font-semibold text-xs sm:text-sm transition-all shadow-md active:scale-95 disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                        {loading ? (
                            <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                        ) : (
                            <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
                                <polyline points="17 21 17 13 7 13 7 21" />
                                <polyline points="7 3 7 8 15 8" />
                            </svg>
                        )}
                        Save Out Time
                    </button>
                </div>
            </div>
        </div>
    );
};

export default SetOutTimeModal;
